// @ts-check

import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  EyeOutlined,
  LinkOutlined,
  MoreOutlined,
  OrderedListOutlined,
  PlusOutlined,
  ScheduleOutlined,
  TagOutlined,
  ThunderboltOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
} from "@ant-design/icons";
import { Button, Card, Col, Collapse, Descriptions, Dropdown, Empty, List, Menu, Modal, Pagination, Row, Select, Space, Spin, Tag, Tooltip, Typography } from "antd";
import { Link } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/tr";
import { createContext, useContext, useMemo, useState } from "react";
import { BiWind } from "react-icons/bi";
import { BsFillPinMapFill } from "react-icons/bs";
import { FaCloudRain, FaSun } from "react-icons/fa";
import { useQuery } from "react-query";
import tr from "../i18n";
import { useGarden } from "./garden-context";
import { ROLE_LABELS } from "../utils/AccessLevels";

dayjs.extend(relativeTime);

// Set dayjs locale based on user preference
const locale = localStorage.getItem("locale") || "tr";
dayjs.locale(locale === "tr" ? "tr" : "en");

const AuditContext = createContext(/** @type {import("../types/audit").AuditContextValue | undefined} */(undefined));

export function useAudit() {
  const context = useContext(AuditContext);
  if (!context) throw new Error(tr("audit.context.missing"));
  return context;
}

export function AuditProvider(/** @type {import("react").PropsWithChildren<{}>} */ { children }) {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [gardenId, setGardenId] = useState(/** @type {string | undefined} */(undefined));
  const [userId, setUserId] = useState(/** @type {string | undefined} */(""));
  const [users, setUsers] = useState(/** @type {Array<{ id: number, name: string }>} */([]));

  const {
    data: auditLogsResponse,
    isFetching: isFetchingAuditLogs,
    refetch: refetchAuditLogs,
  } = /** @type {import("react-query").UseQueryResult<import("axios").AxiosResponse<import("../types/audit").GetAuditLogsResponse>, unknown>} */ (
      useQuery(
        ["AuditLogs", page, limit, gardenId, userId],
        () =>
          axios.get(`${process.env.REACT_APP_PLANNER_API_END_POINT}/audit`, {
            params: Object.fromEntries(Object.entries({ page, limit, gardenId, userId }).filter(([_, v]) => v != null && v !== "")),
          }),
        { refetchOnWindowFocus: false, cacheTime: 0, staleTime: 0, refetchOnMount: true },
      )
    );

  const { data: usersResponse } = useQuery(["FetchAllUsers"], () => axios.get(process.env.REACT_APP_GARDENER_API_END_POINT + "/user"), {
    refetchOnWindowFocus: false,
    onSuccess: (res) => {
      if (!res?.data?.data) return;

      setUsers(
        res.data.data.filter(t => t.role !== "super_admin").map((/** @type {unknown} */ t) => {
          if (typeof t === "object" && t !== null && "id" in t && "name" in t) {
            return { id: /** @type {number} */ (t.id), name: /** @type {string} */ (t.name) };
          }

          throw new Error(tr("audit.context.invalidUserData"));
        }),
      );
    },
  });

  const auditContextValue = useMemo(() => {
    /** @type {import("../types/audit").AuditContextValue} */
    const value = {
      auditLogs: auditLogsResponse?.data?.data || [],
      pagination: auditLogsResponse?.data?.pagination,
      users,
      isFetchingAuditLogs,
      pageState: [page, setPage],
      limitState: [limit, setLimit],
      gardenIdState: [gardenId, setGardenId],
      userIdState: [userId, setUserId],
      refetchAuditLogs,
    };
    return value;
  }, [auditLogsResponse?.data?.data, auditLogsResponse?.data?.pagination, users, isFetchingAuditLogs, page, limit, gardenId, userId, refetchAuditLogs]);

  return <AuditContext.Provider value={auditContextValue}>{children}</AuditContext.Provider>;
}

AuditProvider.GardensSelect = function AuditProviderGardensSelect() {
  const { gardens, isFetchingGardens } = useGarden();
  const { gardenIdState } = useAudit();
  const [gardenId, setGardenId] = gardenIdState;

  if (!gardens) return null;

  return (
    <Select
      allowClear
      autoFocus
      filterOption={(input, option) => {
        const label = /** @type {string} */ (option?.label) || "";
        return label.toLowerCase().includes(input.toLowerCase());
      }}
      loading={isFetchingGardens}
      notFoundContent={
        <Empty
          image={<BsFillPinMapFill style={{ fontSize: 36 }} />}
          imageStyle={{ height: 36, marginTop: 36 }}
          description={
            <Typography.Paragraph style={{ lineHeight: 1.1, marginBottom: 36 }} type="secondary">
              {tr("No gardens found")}
            </Typography.Paragraph>
          }
        />
      }
      onChange={(value) => setGardenId(value)}
      options={gardens.map((garden) => ({ label: garden.name, value: garden.id }))}
      placeholder={tr("Park/Garden Search")}
      showSearch
      style={{ width: 200 }}
      value={gardenId}
    />
  );
};

AuditProvider.UsersSelect = function AuditProviderUsersSelect() {
  const { userIdState, users } = useAudit();
  const [userId, setUserId] = userIdState;

  return (
    <Select
      allowClear
      filterOption={(input, option) => {
        const label = /** @type {string} */ (option?.label) || "";
        return label.toLowerCase().includes(input.toLowerCase());
      }}
      onChange={(value) => setUserId(value)}
      options={[
        { label: tr("All Users"), value: "" },
        ...users.map((user) => ({
          label: user.name,
          value: user.id,
        })),
      ]}
      placeholder={tr("User Search")}
      showSearch
      style={{ width: 200 }}
      value={userId}
    />
  );
};

// Helper for locale-aware pluralization
/**
 * @param {number} count
 * @param {string} enSingular
 * @param {string} enPlural
 * @param {string} trWord
 * @returns {string}
 */
const formatCount = (count, enSingular, enPlural, trWord) => {
  const currentLocale = localStorage.getItem("locale") || "tr";
  if (currentLocale === "tr") {
    return `${count} ${trWord}`;
  }
  return `${count} ${count === 1 ? enSingular : enPlural}`;
};

// Raw field key -> human label. Anything not listed is hidden from end users
// (they shouldn't see internal field names they don't recognize).
/** @type {Record<string, string>} */
const FIELD_LABELS = {
  deviceSerial: "Device",
  name: "Name",
  valveId: "Valve",
  state: "State",
  wateringStatus: "Watering",
  schedulerMode: "Scheduler mode",
  userId: "User",
  gardenId: "Garden",
  sid: "Stock",
  tags: "Tags",
};

// Values that should be translated / prettified rather than shown raw.
/** @param {string} key @param {any} value */
const formatFieldValue = (key, value) => {
  if (value === true) return tr("On");
  if (value === false) return tr("Off");
  if (key === "state") return value === "on" ? tr("On") : tr("Off");
  if (key === "wateringStatus") {
    if (value === "disabled") return tr("Disabled");
    if (value === "enabled" || value === "active") return tr("Enabled");
  }
  return String(value);
};

// Keys removed from the raw view entirely (super-admin included).
const RAW_OMIT_TOP = new Set(["_id", "ips", "ip", "baseUrl", "path", "__v"]);

/**
 * Return a shallow-cleaned copy of the record for the raw view:
 * drops plumbing keys and user.id, without mutating the original.
 * @param {import("../types/audit").AuditLog} record
 */
const sanitizeRaw = (record) => {
  /** @type {Record<string, any>} */
  const clean = {};
  for (const [k, v] of Object.entries(record)) {
    if (RAW_OMIT_TOP.has(k)) continue;
    clean[k] = v;
  }
  if (clean.user && typeof clean.user === "object") {
    const { id, ...restUser } = clean.user; // drop user.id only
    clean.user = restUser;
  }
  return clean;
};

// Keys that are never useful to show in the summary view
const OMIT_KEYS = new Set([
  "_id", "__v", "ips", "ip", "baseUrl", "path", "updatedAt",
  "valves", // handled specially below
]);

/**
 * User-facing parameter rows. Only whitelisted, human-labeled fields.
 * @param {import("../types/audit").AuditLog} record
 * @returns {Array<{ label: string, value: string }>}
 */
const getDetailRows = (record) => {
  const rows = [];
  const seen = new Set();
  const push = (rawKey, value) => {
    const label = FIELD_LABELS[rawKey];
    if (!label) return;                       // hide unknown fields from users
    if (value === undefined || value === null || value === "") return;
    if (seen.has(label)) return;              // avoid dupes across body/query/params
    seen.add(label);
    rows.push({ label: tr(label), value: formatFieldValue(rawKey, value) });
  };

  const { body = {}, query = {}, params = {} } = record;

  // Valve summary: which zones are on, by name (instead of the raw map)
  if (record.originalUrl.includes("/valve") && body.valves) {
    const active = Object.entries(body.valves)
      .filter(([, v]) => v?.state === "on")
      .map(([id, v]) => v.name || `#${id}`);
    rows.push({
      label: tr("Active zones"),
      value: active.length ? active.join(", ") : tr("None"),
    });
  }

  // Optimization toggles as readable On/Off
  if (record.originalUrl.includes("/optimize")) {
    const o = body.optimize || body;
    ["sun", "rain", "wind"].forEach((k) => {
      if (o?.[k] !== undefined) rows.push({ label: tr(k), value: o[k] ? tr("On") : tr("Off") });
    });
  }

  if (body.commits?.length) rows.push({ label: tr("Operations"), value: String(body.commits.length) });

  // Generic whitelisted scalars
  for (const src of [query, params, body]) {
    for (const [k, v] of Object.entries(src)) {
      if (typeof v === "object") continue;
      push(k, v);
    }
  }

  return rows;
};

/**
 * A plain-language sentence describing what happened, for end users.
 * Falls back to the action name if we don't have a specific template.
 * @param {import("../types/audit").AuditLog} record
 * @returns {string}
 */
const getHumanSummary = (record) => {
  const { body = {}, params = {}, query = {} } = record;
  const url = record.originalUrl;

  if (url.includes("/valve/name")) {
    return tr("Renamed valve {n} to \"{name}\"")
      .replace("{n}", body.valveId ?? "?")
      .replace("{name}", body.name || "—");
  }
  if (url.includes("/valve/state")) {
    return tr("Turned valve {n} {state}")
      .replace("{n}", body.valveId ?? "?")
      .replace("{state}", body.state === "on" ? tr("on") : tr("off"));
  }
  if (url.includes("/device/name")) {
    return tr("Renamed device to \"{name}\"").replace("{name}", query.name || body.name || "—");
  }
  // fall back to the existing action label
  return tr(getAction(record.originalUrl, record.method).name);
};

// Method color mapping
/** @type {Record<string, string>} */
const METHOD_COLORS = {
  GET: "#1890ff", // Blue
  POST: "#52c41a", // Green
  PUT: "#fa8c16", // Orange
  DELETE: "#ff4d4f", // Red
};

/** @type {Record<string, Record<string, { name: string }>>} */
const ACTIONS_MAP = {
  "/api/device": { POST: { name: "Device created" }, PUT: { name: "Device updated" }, DELETE: { name: "Device deleted" } },
  "/api/device/location": { PUT: { name: "Device location updated" } },
  "/api/garden": { POST: { name: "Park created" }, PUT: { name: "Park updated" }, DELETE: { name: "Park deleted" }, GET: { name: "Park viewed" } },
  "/api/garden/device": { PUT: { name: "Device assigned" }, DELETE: { name: "Device removed" } },
  "/api/garden/commit-schedules": { POST: { name: "Scheduler plans committed" } },
  "/api/garden/user": { PUT: { name: "User assigned" }, DELETE: { name: "User unassigned" } },
  "/api/garden/optimize": { POST: { name: "Optimization set" } },
  "/api/garden/tags": { PUT: { name: "Park tagged" }, DELETE: { name: "Park untagged" } },
  "/api/garden/active-templates": { PUT: { name: "Template activated" } },
  "/api/plan": { POST: { name: "Plan created" }, DELETE: { name: "Plan deleted" }, GET: { name: "Plan viewed" } },
  "/api/plan/test/watering": { POST: { name: "Test watering" } },
  "/api/plan/manual": { POST: { name: "Manual watering" } },
  "/api/quick-plan-templates": {
    POST: { name: "Quick plan template created" },
    DELETE: { name: "Quick plan template deleted" },
    GET: { name: "Quick plan templates viewed" },
  },
  "/api/tag": { POST: { name: "Tag created" }, DELETE: { name: "Tag deleted" } },
  "/api/inventory": { GET: { name: "Inventory queried" } },
  "/api/device/valve/name": { PUT: { name: "Valve name updated" } },
  "/api/device/valve/state": { PUT: { name: "Valve state updated" } }
};

/**
 * @param {string} url
 * @param {string} method
 * @returns {{ name: string }}
 */
const getAction = (url, method) => {
  const deleteGardenSubStr = ["/device", "/user", "/tags"];
  const defaultAction = { name: `${method} request` };

  // Special URL patterns
  if (url.includes("devices-order")) return { name: "Devices reordered" };
  if (url.includes("/templates") && method === "GET") return { name: "Templates viewed" };
  if (url.includes("/api/device") && url.includes("mgmt") && method === "POST") return { name: "Battery mode updated" };
  if (url.includes("/api/device/name") && method === "PUT") return { name: "Device name updated" };
  if (url.includes("/api/quick-plan-templates") && method === "DELETE") return { name: "Quick plan template deleted" };
  if (url === "/api/garden/plans" && !deleteGardenSubStr.some((ss) => url.includes(ss)) && method === "DELETE") return { name: "Park Plans deleted" };
  else if (url.includes("/api/garden") && !deleteGardenSubStr.some((ss) => url.includes(ss)) && method === "DELETE") return { name: "Park deleted" };

  const correctedUrl = url.split("?")[0].replace(/\/$/, "");
  return ACTIONS_MAP[correctedUrl]?.[method] || defaultAction;
};

/**
 * @param {string} actionName
 * @param {string} method
 * @returns {React.ReactNode}
 */
const getActionIcon = (actionName, method) => {
  const color = METHOD_COLORS[method] || "rgba(0,0,0,0.45)";
  const style = { fontSize: 14, color, marginRight: 6 };

  // Action-specific icons
  if (actionName.includes("reordered")) return <OrderedListOutlined style={style} />;
  if (actionName.includes("Optimization") || actionName.includes("Battery mode")) return <ThunderboltOutlined style={style} />;
  if (actionName.includes("Device assigned") || actionName.includes("Device removed")) return <LinkOutlined style={style} />;
  if (actionName.includes("User assigned")) return <UserAddOutlined style={style} />;
  if (actionName.includes("User unassigned")) return <UserDeleteOutlined style={style} />;
  if (actionName.includes("tagged") || actionName.includes("untagged")) return <TagOutlined style={style} />;
  if (actionName.includes("Scheduler") || actionName.includes("Plan") || actionName.includes("Template")) return <ScheduleOutlined style={style} />;

  // Method-based fallback icons
  switch (method) {
    case "GET":
      return <EyeOutlined style={style} />;
    case "POST":
      return <PlusOutlined style={style} />;
    case "PUT":
      return <EditOutlined style={style} />;
    case "DELETE":
      return <DeleteOutlined style={style} />;
    default:
      return <EyeOutlined style={{ ...style, color: "rgba(0,0,0,0.45)" }} />;
  }
};

/**
 * Get optimization icons for display
 * @param {import("../types/audit").AuditLog} record
 * @returns {React.ReactNode | null}
 */
const getOptimizationIcons = (record) => {
  const { body } = record;
  const optimize = body?.optimize || body;

  if (!record.originalUrl.includes("/optimize")) return null;
  if (optimize?.sun === undefined && optimize?.rain === undefined && optimize?.wind === undefined) return null;

  const iconStyle = { marginLeft: 4, verticalAlign: "middle" };
  const icons = [];

  if (optimize?.sun !== undefined) {
    icons.push(
      <Tooltip key="sun" title={`${tr("Sun")}: ${optimize.sun ? tr("On") : tr("Off")}`}>
        <FaSun style={{ ...iconStyle, color: optimize.sun ? "orange" : "#d9d9d9" }} />
      </Tooltip>,
    );
  }
  if (optimize?.rain !== undefined) {
    icons.push(
      <Tooltip key="rain" title={`${tr("Rain")}: ${optimize.rain ? tr("On") : tr("Off")}`}>
        <FaCloudRain style={{ ...iconStyle, color: optimize.rain ? "lightskyblue" : "#d9d9d9" }} />
      </Tooltip>,
    );
  }
  if (optimize?.wind !== undefined) {
    icons.push(
      <Tooltip key="wind" title={`${tr("Wind")}: ${optimize.wind ? tr("On") : tr("Off")}`}>
        <BiWind style={{ ...iconStyle, color: optimize.wind ? "blueviolet" : "#d9d9d9" }} />
      </Tooltip>,
    );
  }

  return icons.length > 0 ? <Space size={2}>{icons}</Space> : null;
};

/**
 * Get contextual detail string for action
 * @param {import("../types/audit").AuditLog} record
 * @returns {string | null}
 */
const getActionDetail = (record) => {
  const { body, query } = record;
  const details = [];

  // Watering status changes (including "active")
  if (body?.wateringStatus === "disabled") details.push(tr("Watering disabled"));
  else if (body?.wateringStatus === "enabled" || body?.wateringStatus === "active") details.push(tr("Watering enabled"));

  // Scheduler mode changes
  if (body?.schedulerMode) details.push(`${tr("Scheduler")}: ${body.schedulerMode}`);

  // Garden name if present
  if (body?.name && record.originalUrl.includes("/garden")) details.push(body.name);

  // Plan name
  if (body?.name && record.originalUrl.includes("/plan")) details.push(body.name);

  // Scheduler commits count
  if (body?.commits?.length) details.push(`${body.commits.length} ${tr("operation(s)")}`);

  // Device operations
  if (record.originalUrl.includes("/device")) {
    if (query?.sid) details.push(`Stock: ${query.sid}`);
    else if (body?.deviceSerial) details.push(body.deviceSerial);
    if (body?.deviceIds?.length) details.push(formatCount(body.deviceIds.length, "device", "devices", "cihaz"));
  }

  // User operations
  if (body?.userId && record.originalUrl.includes("/user")) details.push(`ID: ${body.userId}`);

  // Tag operations
  if (body?.tags?.length && record.originalUrl.includes("/tags")) details.push(formatCount(body.tags.length, "tag", "tags", "etiket"));

  // Note: Optimization settings are handled separately with icons via getOptimizationIcons

  return details.length > 0 ? details.join(" • ") : null;
};

/**
 * Get record ID for display
 * @param {import("../types/audit").AuditLog} record
 * @returns {string | null}
 */
const getRecordId = (record) => {
  const { body, params, query } = record;
  return body?.id || body?.gardenId || params?.gardenId || params?.id || query?.sid || record._id || null;
};

/**
 * Group logs by time period
 * @param {import("../types/audit").AuditLog[]} logs
 */
const groupLogs = (logs) => {
  const now = dayjs();
  const todayStart = now.startOf("day");
  const yesterdayStart = todayStart.subtract(1, "day");
  const weekStart = todayStart.subtract(7, "day");

  /** @type {{ TODAY: import("../types/audit").AuditLog[], YESTERDAY: import("../types/audit").AuditLog[], LAST_WEEK: import("../types/audit").AuditLog[], OLDER: import("../types/audit").AuditLog[] }} */
  const initial = { TODAY: [], YESTERDAY: [], LAST_WEEK: [], OLDER: [] };

  return logs.reduce((acc, log) => {
    const logDate = dayjs(log.createdAt);
    if (logDate.isAfter(todayStart)) {
      acc.TODAY.push(log);
    } else if (logDate.isAfter(yesterdayStart)) {
      acc.YESTERDAY.push(log);
    } else if (logDate.isAfter(weekStart)) {
      acc.LAST_WEEK.push(log);
    } else {
      acc.OLDER.push(log);
    }
    return acc;
  }, initial);
};



/**
 * Section header component
 * @param {{ title: string }} props
 */
const SectionHeader = ({ title }) => (
  <Typography.Text
    type="secondary"
    strong
    style={{
      fontSize: 11,
      letterSpacing: "0.6px",
      textTransform: "uppercase",
      display: "block",
      marginTop: 16,
      marginBottom: 8,
    }}
  >
    {title}
  </Typography.Text>
);

/**
 * Extract gardenId from audit record
 * @param {import("../types/audit").AuditLog} record
 * @returns {string | null}
 */
const getGardenId = (record) => {
  // Try to get gardenId from body, params, or URL
  if (record.body?.gardenId) return record.body.gardenId;
  if (record.params?.gardenId) return record.params.gardenId;
  if (record.params?.id && record.originalUrl.includes("/garden")) return record.params.id;
  // Check body.id for /api/garden endpoints (like PUT /api/garden with body.id)
  if (record.body?.id && record.originalUrl.includes("/api/garden")) return record.body.id;
  // Try to extract from URL pattern /api/garden/:id (supports alphanumeric IDs like kardesler-89, 000001)
  const match = record.originalUrl.match(/\/api\/garden\/([a-zA-Z0-9_-]+)/);
  if (
    match &&
    !match[1].includes("commit") &&
    !match[1].includes("device") &&
    !match[1].includes("user") &&
    !match[1].includes("tags") &&
    !match[1].includes("optimize") &&
    !match[1].includes("template")
  ) {
    return match[1];
  }
  return null;
};

/**
 * History item component
 * @param {{ record: import("../types/audit").AuditLog, onViewRaw: (record: import("../types/audit").AuditLog) => void }} props
 */
const HistoryItem = ({ record, onViewRaw }) => {
  const action = getAction(record.originalUrl, record.method);
  const createdAt = dayjs(record.createdAt);
  const gardenId = getGardenId(record);
  const actionDetail = getActionDetail(record);
  const optimizationIcons = getOptimizationIcons(record);
  const recordId = getRecordId(record);

  const menuItems = /** @type {any[]} */ ([
    ...(recordId ? [{ key: "id", label: `ID: ${recordId}`, disabled: true }, { type: /** @type {const} */ ("divider") }] : []),
    { key: "showMore", label: tr("Show more"), icon: <EyeOutlined /> },
  ]);

  /** @param {{ key: string }} info */
  const handleMenuClick = ({ key }) => {
    if (key === "showMore") {
      onViewRaw(record);
    }
  };

  return (
    <List.Item style={{ padding: "12px 8px", borderRadius: 8 }}>
      <List.Item.Meta
        avatar={getActionIcon(action.name, record.method)}
        title={
          <Space size={4} style={{ width: "100%", justifyContent: "space-between" }}>
            <Space size={4}>
              <Typography.Text strong style={{ fontSize: 14 }}>
                {tr(action.name)}
                {actionDetail && (
                  <Typography.Text type="secondary" style={{ fontWeight: "normal" }}>
                    : {actionDetail}
                  </Typography.Text>
                )}
                {optimizationIcons}
              </Typography.Text>
              {record.statusCode >= 400 && (
                <Tag color="error" style={{ marginLeft: 4, fontSize: 11 }}>
                  {tr("Error")} {record.statusCode}
                </Tag>
              )}
              {record.statusCode === 304 && (
                <Tag color="default" style={{ marginLeft: 4, fontSize: 11 }}>
                  {tr("Not Modified")}
                </Tag>
              )}
            </Space>
            <Space size={4}>
              {gardenId && (
                <Link to={`/control-center/gardens/${gardenId}`}>
                  <Button type="link" size="small" icon={<EnvironmentOutlined />} style={{ padding: "0 4px" }}>
                    {tr("Go to garden")}
                  </Button>
                </Link>
              )}
              <Dropdown overlay={<Menu items={menuItems} onClick={handleMenuClick} />} trigger={["click"]}>
                <Button type="text" size="small" icon={<MoreOutlined style={{ fontSize: 16 }} />} />
              </Dropdown>
            </Space>
          </Space>
        }
        description={
          <Space direction="vertical" size={2} style={{ width: "100%" }}>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {tr("User")}: {record.user?.name || tr("Unknown user")} ({tr(ROLE_LABELS[record.user?.role]) || "-"})
            </Typography.Text>
            <Tooltip title={createdAt.format("DD/MM/YYYY HH:mm:ss")}>
              <Typography.Text type="secondary" style={{ fontSize: 12, color: "rgba(0,0,0,0.35)" }}>
                {createdAt.fromNow()}
              </Typography.Text>
            </Tooltip>
          </Space>
        }
      />
    </List.Item>
  );
};

AuditProvider.Table = function AuditProviderTable() {
  const { auditLogs, pagination, isFetchingAuditLogs, pageState, limitState } = useAudit();
  const [page, setPage] = pageState;
  const [limit, setLimit] = limitState;
  const [viewRecord, setViewRecord] = useState(/** @type {import("../types/audit").AuditLog | null} */(null));

  const grouped = groupLogs(auditLogs);
  const sections = [
    { key: "TODAY", title: tr("Today"), logs: grouped.TODAY },
    { key: "YESTERDAY", title: tr("Yesterday"), logs: grouped.YESTERDAY },
    { key: "LAST_WEEK", title: tr("Last Week"), logs: grouped.LAST_WEEK },
    { key: "OLDER", title: tr("Older"), logs: grouped.OLDER },
  ].filter((s) => s.logs.length > 0);

  return (
    <Card bordered={false} bodyStyle={{ padding: "16px 20px" }} style={{ borderRadius: 10 }}>
      <Spin spinning={isFetchingAuditLogs}>
        {auditLogs.length === 0 && !isFetchingAuditLogs ? (
          <Empty description={tr("No audit logs found")} style={{ padding: 40 }} />
        ) : (
          <List split style={{ maxHeight: "70vh", overflowY: "auto" }} itemLayout="horizontal">
            {sections.map((section, sectionIndex) => (
              <List.Item key={section.key} style={{ display: "block", padding: 0, border: "none" }}>
                <SectionHeader title={section.title} />
                {section.logs.map((record) => (
                  <HistoryItem key={record._id} record={record} onViewRaw={setViewRecord} />
                ))}
              </List.Item>
            ))}
          </List>
        )}
      </Spin>

      <Row justify="end" style={{ marginTop: 16 }}>
        <Col>
          <Pagination
            current={page}
            pageSize={limit}
            total={pagination?.total || 0}
            onChange={(p, l) => {
              setPage(p);
              setLimit(l);
            }}
            showSizeChanger
            size="small"
            showTotal={(total) => `${tr("Total")} ${total}`}
          />
        </Col>
      </Row>

      <Modal
        title={viewRecord ? getHumanSummary(viewRecord) : tr("Details")}
        visible={!!viewRecord}
        onCancel={() => setViewRecord(null)}
        footer={[<Button key="close" onClick={() => setViewRecord(null)}>{tr("Close")}</Button>]}
        width={480}
        centered
        bodyStyle={{ padding: "16px 20px" }}
      >
        {viewRecord && (
          <Space direction="vertical" size={12} style={{ width: "100%" }}>
            {/* Failure notice only — success needs no status code */}
            {viewRecord.statusCode >= 400 && (
              <Tag color="error">{tr("This action did not complete successfully.")}</Tag>
            )}

            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label={tr("Performed by")}>
                {viewRecord.user?.name || tr("Unknown user")}
                {viewRecord.user?.role ? ` · ${tr(ROLE_LABELS[viewRecord.user.role]) || viewRecord.user.role}` : ""}
              </Descriptions.Item>
              <Descriptions.Item label={tr("Date")}>
                {dayjs(viewRecord.createdAt).format("DD/MM/YYYY HH:mm")}
              </Descriptions.Item>
              {getDetailRows(viewRecord).map((r, i) => (
                <Descriptions.Item key={i} label={r.label}>{r.value}</Descriptions.Item>
              ))}
            </Descriptions>

            {/* Super-admin only — gate this later */}
            {(
              <Collapse ghost>
                <Collapse.Panel header={tr("Raw Data")} key="raw">
                  <Typography.Paragraph
                    code copyable
                    style={{
                      maxHeight: "40vh", overflow: "auto", margin: 0, whiteSpace: "pre-wrap",
                      fontSize: 12, background: "#f5f5f5", padding: 16, borderRadius: 8
                    }}
                  >
                    {JSON.stringify(sanitizeRaw(viewRecord), null, 2)}
                  </Typography.Paragraph>
                </Collapse.Panel>
              </Collapse>
            )}
          </Space>
        )}
      </Modal>
    </Card>
  );
};
